import { useEffect, useRef, useState } from 'react';

export const useInfiniteScroll = (speed: number, isPaused: boolean = false) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(0);
  const animationRef = useRef<number>();
  const lastTimeRef = useRef<number>(0);
  const containerWidthRef = useRef<number>(0);
  const hasMeasuredRef = useRef<boolean>(false);
  const isPausedRef = useRef(isPaused);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    let isCancelled = false;
    let idleCallbackId: number | undefined;
    let resizeObserver: ResizeObserver | undefined;

    // Delay measurement to avoid forced reflow during initial layout
    const measureContainer = () => {
      if (containerRef.current && !hasMeasuredRef.current && !isCancelled) {
        // Batch read to minimize reflows
        requestAnimationFrame(() => {
          if (containerRef.current && !isCancelled) {
            // Get the width of one set of items (scrollWidth / 3 since we render 3 copies)
            containerWidthRef.current = containerRef.current.scrollWidth / 3;
            hasMeasuredRef.current = true;
          }
        });
      }
    };

    // Use ResizeObserver for efficient size tracking
    if ('ResizeObserver' in window && containerRef.current) {
      resizeObserver = new ResizeObserver((entries) => {
        if (!isCancelled) {
          // Use requestAnimationFrame to batch the measurement
          requestAnimationFrame(() => {
            const entry = entries[0];
            if (entry && entry.target) {
              // Get the width of one set of items (scrollWidth / 3 since we render 3 copies)
              containerWidthRef.current = (entry.target as HTMLElement).scrollWidth / 3;
              hasMeasuredRef.current = true;
            }
          });
        }
      });
      resizeObserver.observe(containerRef.current);
    }

    // Use requestIdleCallback to defer initial measurement
    if ('requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(measureContainer, { timeout: 500 });
    } else {
      setTimeout(measureContainer, 200);
    }

    const animate = (currentTime: number) => {
      if (isCancelled) return;

      if (!lastTimeRef.current) {
        lastTimeRef.current = currentTime;
      }

      if (!isPausedRef.current) {
        const deltaTime = currentTime - lastTimeRef.current;
        lastTimeRef.current = currentTime;

        setPosition((prevPosition) => {
        const movement = (speed * deltaTime) / 16;
        let newPosition = prevPosition + movement;
        
        // Use cached container width (no DOM read in animation loop)
        const containerWidth = containerWidthRef.current;
        if (containerWidth > 0) {
          if (speed > 0) {
            newPosition = newPosition % containerWidth;
          } else {
            newPosition = ((newPosition % containerWidth) + containerWidth) % containerWidth;
          }
        }
        
        return newPosition;
        });
      } else {
        lastTimeRef.current = currentTime;
      }

      if (!isCancelled) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    // Delay animation start to ensure measurement is complete
    const startAnimationTimeout = setTimeout(() => {
      if (!isCancelled) {
        animationRef.current = requestAnimationFrame(animate);
      }
    }, 100);

    return () => {
      isCancelled = true;
      clearTimeout(startAnimationTimeout);
      if (idleCallbackId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [speed]);

  return { containerRef, position };
};
